// Thin wrapper over amazon-cognito-identity-js for the Phase 3 login flow.
// Handles: sign in, NEW_PASSWORD_REQUIRED challenge, sign out, current session
// retrieval. The id token gets put on every API request via api.ts (Authorization
// header — wired in Phase 3 part 2).

import {
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';

const USER_POOL_ID = 'us-east-1_JEdkK7d2A';
const CLIENT_ID = '432eom42vi6297kafa4kn14f8n';
const REGION = 'us-east-1';

export const userPool = new CognitoUserPool({
  UserPoolId: USER_POOL_ID,
  ClientId: CLIENT_ID,
});

export type CognitoSessionInfo = {
  email: string;
  sub: string;
  idToken: string;
  accessToken: string;
};

export function getCurrentSession(): Promise<CognitoSessionInfo | null> {
  return new Promise((resolve) => {
    const user = userPool.getCurrentUser();
    if (!user) return resolve(null);
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) return resolve(null);
      const idToken = session.getIdToken();
      const payload = idToken.payload as { email: string; sub: string };
      resolve({
        email: payload.email,
        sub: payload.sub,
        idToken: idToken.getJwtToken(),
        accessToken: session.getAccessToken().getJwtToken(),
      });
    });
  });
}

export type SignInResult =
  | { kind: 'success'; session: CognitoSessionInfo }
  | { kind: 'newPasswordRequired'; user: CognitoUser; userAttributes: any };

export function signIn(email: string, password: string): Promise<SignInResult> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: userPool });
    const auth = new AuthenticationDetails({ Username: email, Password: password });
    user.authenticateUser(auth, {
      onSuccess: (session) => {
        const idToken = session.getIdToken();
        const payload = idToken.payload as { email: string; sub: string };
        resolve({
          kind: 'success',
          session: {
            email: payload.email,
            sub: payload.sub,
            idToken: idToken.getJwtToken(),
            accessToken: session.getAccessToken().getJwtToken(),
          },
        });
      },
      onFailure: (err) => reject(err),
      newPasswordRequired: (userAttributes) => {
        // Cognito requires confirmation of attributes; strip non-mutable ones
        delete userAttributes.email_verified;
        delete userAttributes.email; // Cognito complains if you re-set
        resolve({ kind: 'newPasswordRequired', user, userAttributes });
      },
    });
  });
}

export function completeNewPasswordChallenge(
  user: CognitoUser,
  newPassword: string,
  userAttributes: any,
): Promise<CognitoSessionInfo> {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(newPassword, userAttributes, {
      onSuccess: (session) => {
        const idToken = session.getIdToken();
        const payload = idToken.payload as { email: string; sub: string };
        resolve({
          email: payload.email,
          sub: payload.sub,
          idToken: idToken.getJwtToken(),
          accessToken: session.getAccessToken().getJwtToken(),
        });
      },
      onFailure: (err) => reject(err),
    });
  });
}

export function signOut(): void {
  const user = userPool.getCurrentUser();
  if (user) user.signOut();
}

export function getIdToken(): Promise<string | null> {
  return getCurrentSession().then((s) => s?.idToken ?? null);
}

export const COGNITO_REGION = REGION;
